import React, { useRef, useEffect, MouseEvent } from 'react';
import { createEmitAndSemanticDiagnosticsBuilderProgram } from 'typescript';

interface CanvasItemProps {
  width: number;
  height: number;
  index: number;
  ticker: string;
  title: string;
  chartData: any;
  latestPrice?: number;
  onPriceChange: (term: number) => void;
  matchViewportWidth?: boolean
}

const CanvasItem = ({
  width,
  height,
  index,
  ticker,
  title,
  chartData,
  latestPrice = -1,
  onPriceChange,
  matchViewportWidth = false
}: CanvasItemProps): JSX.Element => {
  var enterMode = false;
  var exitMode = false;
  var addAnchorMode = false;
  var entryPrice = -1;
  var exitPrice = -1;
  var currentPriceFromMouse = -1;

  var toggleEnterMode = () => {
    enterMode = !enterMode;
  };

  var toggleAddAnchorMode = () => {
    addAnchorMode = !addAnchorMode;
  };

  var toggleExitMode = () => {
    exitMode = !exitMode;
  };

  var handleChartClick = () => {
 
    if (enterMode == true && exitMode == false) {
      entryPrice = currentPriceFromMouse;
      enterMode = false;
      exitMode = false;
    }
    if (enterMode == false && exitMode == true) {
      exitPrice = currentPriceFromMouse;
      enterMode = false;
      exitMode = false;
    }
  };

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  var symbol = ticker;

  if (!chartData['volume']) {
  }
  var volumes = chartData['volume'];
  var opens = chartData['open'];
  var closes: number[] = chartData['close'];
  var highs = chartData['high'];
  var lows: number[] = chartData['low'];
  var timestamps: number[] = chartData['timestamp'];
  var canvasSaved: boolean = false;

  var priceSectionHeight = height * 0.8;
  var volumeSectionHeight = height * 0.2;
  if(matchViewportWidth){
    width = window.innerWidth
  }
  var chartWidth = width * 0.9;
  var candleWidth = chartWidth / timestamps.length;

  var minPrice = Math.min(...lows);
  var maxPrice = Math.max(...highs);
  var maxVolume = Math.max(...volumes);

  var upDownColorArr: string[] = [];

  var getPriceByY = (y: number) => {
    var percentageFromTop = y / priceSectionHeight;
    var highMinusLow = maxPrice - minPrice;
    return maxPrice - percentageFromTop * highMinusLow;
  };

  var getYByPrice = (pricePoint: number) => {
    var highMinusLow = maxPrice - minPrice;
    var priceMinusLow = pricePoint - minPrice;
    var fromTop = highMinusLow - priceMinusLow;
    var percentageFromTop = fromTop / highMinusLow;
    return percentageFromTop * priceSectionHeight;
  };

  var drawVolume = (
    ctx: CanvasRenderingContext2D,
    index: number,
    fromLeft: number,
  ) => {
    var volComparedToMaxVol = volumes[index] / maxVolume;

    var volumeYStart = height - volumeSectionHeight;
    var volY =
      volumeYStart +
      (volumeSectionHeight - volComparedToMaxVol * volumeSectionHeight);
    if (volumes[index] > 0) {
      var volLineCenterX = fromLeft + candleWidth / 2;
      ctx.beginPath();
      ctx.strokeStyle = upDownColorArr[index];
      ctx.fillStyle = upDownColorArr[index];

      ctx.lineWidth = candleWidth;
      ctx.moveTo(volLineCenterX, volY);
      ctx.lineTo(volLineCenterX, height);
      ctx.stroke();
    }
  };

  var drawSmaLine = (ctx: CanvasRenderingContext2D) => {
    // start price lines and text
    var smaPrices = chartData.sma;
    var priorStrokeStyle = ctx.strokeStyle;
    var lineWidth = candleWidth / 2;
    var priorLineWidth = ctx.lineWidth;
    if (smaPrices) {
      ctx.strokeStyle = 'yellow';
      ctx.lineWidth = lineWidth;
      var currCount = 0;
      var prevYVal = -1;
      var currYVal = -1;
      var currX = 0;
      for (var i = 0; i < smaPrices.length; i++) {
        if (currCount > 0) {
          prevYVal = currYVal;
        }
        var smaPrice = smaPrices[i];
        currYVal = getYByPrice(smaPrice);
        smaPrice = parseFloat(smaPrice.toFixed(2));
        if (currCount > 0) {
          ctx.lineTo(currX, currYVal);
        } else {
          ctx.beginPath();
          ctx.moveTo(currX, currYVal);
        }
        currX += candleWidth;

        currCount++;
      }
      ctx.stroke();
    }
    ctx.strokeStyle = priorStrokeStyle;
    ctx.lineWidth = priorLineWidth;
  };

  var drawPrice = (ctx: CanvasRenderingContext2D) => {
    // draw price right side gradient
    const gradient = ctx.createLinearGradient(chartWidth, 0, width, 0); // Horizontal gradient
    gradient.addColorStop(0, 'black'); // Start color (left side)
    gradient.addColorStop(1, 'rgba(100,100,100,0.5)'); // End color (right side)

    ctx.fillStyle = gradient; // Apply the gradient as the fill style
    ctx.fillRect(chartWidth, 0, width, height); // Draw the rectangle

    // start price lines and text
    var priceDiff = maxPrice - minPrice;
    var priceLineInterval = priceDiff / 5;
    for (var i = 0; i < 5; i++) {
      var bottomPrice = minPrice + i * priceLineInterval;
      var yVal = getYByPrice(bottomPrice);

      bottomPrice = parseFloat(bottomPrice.toFixed(2));

      ctx.strokeStyle = 'rgba(255, 165, 0, 1)';

      ctx.font = '18px Courier';
      ctx.fillStyle = 'orange';
      ctx.fillText(`${bottomPrice}`, width - 50, yVal + 16, 50);
    }
  };

  function drawPercentageChange(
    ctx: CanvasRenderingContext2D,
    priceTwoDaysAgo: number,
    priceToday: number,
    x: number,
    y: number,
  ): void {
    if (priceTwoDaysAgo === 0) {
      throw new Error('The price two days ago cannot be zero.');
    }

    // Calculate the percentage change
    const change = ((priceToday - priceTwoDaysAgo) / priceTwoDaysAgo) * 100;

    // Determine the color and format
    const color = change >= 0 ? 'green' : 'red';
    const formattedChange = `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;

    // Set text style and color
    ctx.fillStyle = color;
    ctx.font = '22px Arial';

    // Draw the text on the canvas
    ctx.fillText(formattedChange, x, y);
  }

  var drawChart = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');

    var barWidth = chartWidth / timestamps.length;

    if (ctx) {
      ctx.globalAlpha = 1.0;

      ctx.clearRect(0, 0, chartWidth, height);
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, chartWidth, height);
      ctx.fill();
      var fromLeft = 0;
      var candleIndex = 0;

      if (chartData['sma']) {
        drawSmaLine(ctx);
      }

      for (const i in timestamps) {
        drawPrice(ctx);
        drawCandle(ctx, candleIndex, fromLeft);
        drawVolume(ctx, candleIndex, fromLeft);
        candleIndex++;
        fromLeft += barWidth;
      }

      drawTopBar(ctx, symbol, closes);

      if(latestPrice && latestPrice > -1){
        var latestPriceY = getYByPrice(latestPrice);
        ctx.strokeStyle = 'yellow';
        ctx.lineWidth = candleWidth / 3;
        ctx.moveTo(0, latestPriceY);
        ctx.lineTo(chartWidth, latestPriceY);
        ctx.stroke();
      }
    }
  };

  var drawAvwapFromDaysBack = (
    daysBack: number,
    ctx: CanvasRenderingContext2D,
  ) => {
    var candlesBack = daysBack;


    if (candlesBack > 0 && candlesBack <= timestamps.length) {
      var totalVolume = 0;
      var totalPriceCumulative = 0;
      var avWapX = chartWidth - candlesBack * candleWidth;

      var candleCount = timestamps.length;
      var daysBackVolumes = volumes.slice(
        candleCount - candlesBack,
        candleCount,
      );
      var daysBackCloses = closes.slice(candleCount - candlesBack, candleCount);

      var lastAvWapPrice = 0;
      var currCandle: number = -1;
      var prevCandle: number = -1;
      var prevCumulative: number = -1;
      var prevTotalVolume: number = -1;

      var avwappIndex = 0;
      for (var daysBackVolume of daysBackVolumes) {
        currCandle = daysBackVolume;
        prevTotalVolume = totalVolume;
        totalVolume += daysBackVolume;
        var priceValue = 0.0;
        priceValue = daysBackCloses[avwappIndex];

        if (prevCandle == null) {
          prevCumulative = priceValue * daysBackVolume;
        } else {
          prevCumulative = totalPriceCumulative;
        }
        totalPriceCumulative += priceValue * daysBackVolume;
        var avWpPrice = totalPriceCumulative / totalVolume;
        avWapX += candleWidth;

        if (prevCandle != null) {
          var prevAvWapPrice = prevCumulative / prevTotalVolume;
          if (prevAvWapPrice > 0 && avWapX > 0) {
            ctx.beginPath();
            ctx.strokeStyle = 'magenta';
            ctx.lineWidth = candleWidth * 0.25;
            ctx.moveTo(avWapX - candleWidth, getYByPrice(prevAvWapPrice));
            ctx.lineTo(avWapX, getYByPrice(avWpPrice));
            ctx.stroke();
          }
        }
        lastAvWapPrice = avWpPrice;
        prevCandle = currCandle;
        avwappIndex++;
      }

      if (lastAvWapPrice > 0) {
        lastAvWapPrice = (lastAvWapPrice * 100) / 100;
        ctx.font = '20px Arial';
        ctx.fillStyle = 'green';
        ctx.fillText('AVWAP: ' + parseFloat(lastAvWapPrice.toFixed(2)), 30, 40);
      }
    }
  };

  function drawTopBar(
    ctx: CanvasRenderingContext2D,
    symbol: any,
    closes: number[],
  ) {
   
    ctx.font = '22px Arial';
    ctx.fillStyle = 'yellow';

    // draw last price

    if (closes.length > 1) {
      var lastClosingPrice = closes[closes.length - 1];
      var secondToLastClosingPrice = closes[closes.length - 2];
      lastClosingPrice = parseFloat(lastClosingPrice.toFixed(2));
      ctx.fillText(`$${lastClosingPrice}`, 100, 20);
      drawPercentageChange(
        ctx,
        secondToLastClosingPrice,
        lastClosingPrice,
        200,
        20,
      );
    }
  }

  var drawCandle = (
    ctx: CanvasRenderingContext2D,
    index: number,
    fromLeft: number,
  ) => {
    var candleColor = 'green';
    var candleTopOpenOrClose = closes[index];
    var candleBottomOpenOrClose = opens[index];
    if (opens[index] >= closes[index]) {
      candleColor = 'red';
      candleTopOpenOrClose = opens[index];
      candleBottomOpenOrClose = closes[index];
    }
    upDownColorArr.push(candleColor);

    var rectTop = getYByPrice(candleTopOpenOrClose!!);
    var rectBottom = getYByPrice(candleBottomOpenOrClose!!);

    var wickTopY = getYByPrice(highs[index]);
    var wickBottomY = getYByPrice(lows[index]);

    var wickWidth = candleWidth / 3;
    var wickX = fromLeft + candleWidth / 2;
    //var candleRectOffset = Offset(topLeftOffset.x, rectTop)

    ctx.strokeStyle = 'grey';
    ctx.lineWidth = wickWidth;

    ctx.beginPath();
    ctx.moveTo(wickX, wickTopY);
    ctx.lineTo(wickX, wickBottomY);
    ctx.stroke();

    ctx.beginPath();
    ctx.fillStyle = candleColor;
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'black';
    ctx.rect(fromLeft, rectTop, candleWidth, rectBottom - rectTop);
    ctx.fill();


  };

  useEffect(() => {
    const canvas = canvasRef.current; // Valid reference here
    if (canvas) {
      drawChart(canvas);
    }
  }, [width, height, index]);




  const handleMouseMove = (e: MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ctx = canvas.getContext('2d');

      if (ctx) {

        ctx.clearRect(0, 0, width, height);

        drawChart(canvas);

        if (entryPrice != -1) {
          ctx.strokeStyle = 'green';
          ctx.lineWidth = 2;
          var entryY = getYByPrice(entryPrice);
          ctx.moveTo(0, entryY);
          ctx.lineTo(chartWidth, entryY);
          ctx.stroke();
        }

        if (exitPrice != -1) {
          ctx.strokeStyle = 'red';
          ctx.lineWidth = 2;
          var exitY = getYByPrice(exitPrice);
          ctx.moveTo(0, exitY);
          ctx.lineTo(chartWidth, exitY);
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.lineWidth = candleWidth * 0.25;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();

        ctx.beginPath();
        ctx.lineWidth = candleWidth * 0.25;
        if (enterMode) {
          ctx.strokeStyle = 'green';
        } else if (exitMode) {
          ctx.strokeStyle = 'red';
        } else {
          ctx.strokeStyle = 'yellow';
        }

        ctx.moveTo(0, y);
        ctx.lineTo(chartWidth, y);
        ctx.stroke();
        currentPriceFromMouse = getPriceByY(y);
        onPriceChange(currentPriceFromMouse)

        var daysBack = Math.ceil((chartWidth - x) / candleWidth);
        drawAvwapFromDaysBack(daysBack, ctx);

        // curr price rect
        ctx.fillStyle = 'black';
        ctx.fillRect(width - 50, y, width - chartWidth, 25);

        //price line
        var price = getPriceByY(y);
        price = parseFloat(price.toFixed(2));
        ctx.font = '24px courier';
        ctx.fillStyle = 'yellow';
        ctx.fillText(`${price}`, width - 50, y + 16, 50);
      }
    }
  };


  useEffect(() => {
      const canvas = canvasRef.current
      if(!canvas || !matchViewportWidth){
        return
      }
      canvas.style.width = '100vw'
      canvas.width = window.innerWidth

  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if(!canvas){
      return
    }
    
    if (canvas && canvas.getContext("2d")) {
      var ctx = canvas.getContext("2d");
      if(ctx != null){
        ctx.clearRect(0, 0, width, height);
        drawChart(canvas);
      }
    
    }
    

}, [latestPrice])

  return (
    <div>
      {/*
      <button onClick={toggleAddAnchorMode}>Add Anchor</button>
      <button onClick={toggleEnterMode}>Enter</button>
      <button onClick={toggleExitMode}>Exit</button>
      */}

      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ border: '1px solid #ccc'
              

         }}
        onClick={handleChartClick}
        onMouseMove={handleMouseMove}
       
      />
    </div>
  );
};

export default CanvasItem;
